// ExpertDetail.tsx - Expert Detay Sayfasi
// App Layout (Sidebar + Topbar + Footer)

import { ArrowLeft, Calendar, Eye, FileText, MapPin, Sparkles, Target, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Topbar from '../components/layout/Topbar'
import { Expert } from '../types/expert'

export default function ExpertDetail() {
  const { expertId } = useParams<{ expertId: string }>()
  const navigate = useNavigate()
  const [expert, setExpert] = useState<Expert | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (expertId) {
      void fetchExpert(expertId)
    }
  }, [expertId])

  const fetchExpert = async (id: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/v1/experts/${id}`)
      const data = await response.json() as Expert
      setExpert(data)
    } catch (error) {
      console.error('Expert fetch error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen" style={{ background: 'var(--body-bg)' }}>        <div className="flex-1 flex flex-col">
          <Topbar />
          <main className="flex-1 flex items-center justify-center">
            <div style={{ color: 'var(--text-secondary)' }}>Yukleniyor...</div>
          </main>
        </div>
      </div>
    )
  }

  if (!expert) {
    return (
      <div className="flex h-screen" style={{ background: 'var(--body-bg)' }}>        <div className="flex-1 flex flex-col">
          <Topbar />
          <main className="flex-1 flex items-center justify-center">
            <div style={{ color: 'var(--text-secondary)' }}>Uzman bulunamadi</div>
          </main>
        </div>
      </div>
    )
  }

  const years = expert.death_year
    ? `${expert.birth_year}-${expert.death_year}`
    : `${expert.birth_year}-gunumuze`

  return (
    <div className="flex h-screen" style={{ background: 'var(--body-bg)' }}>      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar />

        <main className="flex-1 overflow-auto p-6">
          {/* Back Button */}
          <button
            onClick={() => navigate('/experts')}
            className="flex items-center gap-2 mb-6 px-4 py-2 rounded hover:opacity-80"
            style={{
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          >
            <ArrowLeft size={20} />
            Geri
          </button>

          <div className="max-w-4xl mx-auto">
            {/* Header Card */}
            <div className="card mb-6" style={{ background: 'var(--card-bg)' }}>
              <div className="card-body p-6">
                <div className="flex items-start gap-6">
                  {/* Avatar */}
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--primary-color)', color: 'white' }}
                  >
                    <User size={48} />
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {expert.name}
                    </h1>

                    <div className="flex flex-wrap gap-4 mb-4">
                      <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                        <Calendar size={16} />
                        <span>{years}</span>
                      </div>
                      {expert.nationality && (
                        <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                          <MapPin size={16} />
                          <span>{expert.nationality}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <span
                        className="badge badge-primary"
                        style={{
                          background: 'var(--primary-color)',
                          color: 'white',
                          padding: '6px 16px',
                          borderRadius: '4px'
                        }}
                      >
                        {expert.category_id}
                      </span>
                      <span
                        className="text-sm font-semibold"
                        style={{ color: 'var(--primary-color)' }}
                      >
                        Kalite: {expert.quality_baseline}/100
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bio Short */}
            {expert.bio_short && (
              <div className="card mb-6" style={{ background: 'var(--card-bg)' }}>
                <div className="card-body p-6">
                  <p className="text-lg" style={{ color: 'var(--text-primary)' }}>
                    {expert.bio_short}
                  </p>
                </div>
              </div>
            )}

            {/* Uzmanlik Alanlari */}
            {expert.sub_domains && expert.sub_domains.length > 0 && (
              <div className="card mb-6" style={{ background: 'var(--card-bg)' }}>
                <div className="card-body p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Target size={20} />
                    Uzmanlik Alanlari
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {expert.sub_domains.map((domain, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded text-sm"
                        style={{
                          background: 'var(--primary-color-light)',
                          color: 'var(--primary-color)',
                          border: '1px solid var(--primary-color)'
                        }}
                      >
                        {domain}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Persona Bilgileri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Thinking Style */}
              {expert.thinking_style && (
                <div className="card" style={{ background: 'var(--card-bg)' }}>
                  <div className="card-body p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <Sparkles size={18} />
                      Dusunme Tarzi
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {expert.thinking_style}
                    </p>
                  </div>
                </div>
              )}

              {/* Signature Question */}
              {expert.signature_question && (
                <div className="card" style={{ background: 'var(--card-bg)' }}>
                  <div className="card-body p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <FileText size={18} />
                      Imza Sorusu
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {expert.signature_question}
                    </p>
                  </div>
                </div>
              )}

              {/* Lens */}
              {expert.lens && (
                <div className="card" style={{ background: 'var(--card-bg)' }}>
                  <div className="card-body p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <Eye size={18} />
                      Bakis Acisi
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {expert.lens}
                    </p>
                  </div>
                </div>
              )}

              {/* Typical Output */}
              {expert.typical_output && (
                <div className="card" style={{ background: 'var(--card-bg)' }}>
                  <div className="card-body p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <FileText size={18} />
                      Tipik Cikti
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {expert.typical_output}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Bio Long */}
            {expert.bio_long && (
              <div className="card" style={{ background: 'var(--card-bg)' }}>
                <div className="card-body p-6">
                  <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Tam Biyografi
                  </h2>
                  <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {expert.bio_long}
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
